import { useCallback, useEffect, useMemo, useState } from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { APIError, useRealmAPIContext, useRealmAuthContext } from '@interxfi/api-client';
import { useIxmAgentauthOauthV1AgentAuthServiceApproveAuthorizationRequest } from '@interxfi/api-client/generated/hooks/ixm-agentauth-oauth-v1-agent-auth-service-approve-authorization-request';
import { useIxmAgentauthOauthV1AgentAuthServiceDenyAuthorizationRequest } from '@interxfi/api-client/generated/hooks/ixm-agentauth-oauth-v1-agent-auth-service-deny-authorization-request';
import { useIxmAgentauthOauthV1AgentAuthServiceInspectAuthorizationRequest } from '@interxfi/api-client/generated/hooks/ixm-agentauth-oauth-v1-agent-auth-service-inspect-authorization-request';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { Skeleton } from '@interxfi/design-system/components/loading/skeleton';
import { useAddToast } from '@interxfi/design-system/components/toast/toast-context';
import { Typography } from '@interxfi/design-system/components/typography';
import type { IxmUserauthV1JoinedRealmAccess } from '@interxfi/types';
import { ConsentErrorCard } from '../../features/agent-auth/consent-error-card';
import { ConsentForm } from '../../features/agent-auth/consent-form';
import {
  clearConsentRequest,
  parseConsentFragment,
  readConsentRequest,
  resolveConsentAccesses,
  writeConsentRequest,
} from '../../features/agent-auth/consent-request-storage';
import { AuthLayout } from '../../features/auth/layout';
import { DEFAULT_UNAUTHENTICATED_ROUTE, MFA_SETUP_ROUTE } from '../../util/routing/defaults';
import type { RouteGuardOptions } from '../../util/routing/guard';
import { getPageHead } from '../../util/routing/meta';

const CONSENT_PATH = '/oauth/consent';

export const Route = createFileRoute('/oauth/consent')({
  staticData: {
    guard: {
      auth: {
        requireAuthenticated: true,
        redirectToIfNotMet: DEFAULT_UNAUTHENTICATED_ROUTE,
        redirectToIfMfaRequirementNotMet: MFA_SETUP_ROUTE,
      },
    },
  } as const satisfies RouteGuardOptions,
  // agentauth sends the browser here with the signed request in the URL fragment.
  // Move it into sessionStorage and drop the fragment before the auth guard runs,
  // so the login redirect only carries the bare path and the token leaves the URL and history.
  beforeLoad: ({ location }) => {
    if (!location.hash) {
      return;
    }

    const captured = parseConsentFragment(location.hash);
    if (captured) {
      writeConsentRequest(captured);
    }

    throw redirect({ to: CONSENT_PATH, replace: true });
  },
  // Keep the portal URL out of the Referer header when the browser leaves for the client's callback.
  head: () => ({
    meta: [...getPageHead('Connect with Interchange MCP').meta, { name: 'referrer', content: 'no-referrer' }],
  }),
  component: OAuthConsent,
});

function getHostname(value: string) {
  try {
    return new URL(value).hostname;
  } catch {
    return value;
  }
}

function OAuthConsent() {
  const addToast = useAddToast();
  const realmAuthContext = useRealmAuthContext();
  const [consentRequest] = useState(readConsentRequest);
  const [leavingAfter, setLeavingAfter] = useState<'approve' | 'deny'>();
  // Issuer says which zone the request came from. Pick that zone's accesses from whoami
  const { keyAccess, tenantAccesses } = useMemo(
    () => resolveConsentAccesses(realmAuthContext?.accesses, consentRequest?.iss ?? ''),
    [realmAuthContext?.accesses, consentRequest?.iss],
  );
  // Approve and deny must carry the chosen tenant's token: agentauth only accepts keys from the caller's tenant.
  const [tenantAccess, setTenantAccess] = useState<IxmUserauthV1JoinedRealmAccess>();
  const {
    mutate: inspect,
    data: inspection,
    error: inspectError,
  } = useIxmAgentauthOauthV1AgentAuthServiceInspectAuthorizationRequest(undefined, keyAccess);
  const {
    mutate: approve,
    error: approveError,
    isPending: isApproving,
  } = useIxmAgentauthOauthV1AgentAuthServiceApproveAuthorizationRequest(
    {
      onSuccess: (response) => {
        if (!response?.redirectTo) {
          return;
        }

        setLeavingAfter('approve');
        clearConsentRequest();
        window.location.assign(response.redirectTo);
      },
    },
    tenantAccess ?? keyAccess,
  );
  const {
    mutate: deny,
    error: denyError,
    isPending: isDenying,
  } = useIxmAgentauthOauthV1AgentAuthServiceDenyAuthorizationRequest(
    {
      onSuccess: (response) => {
        if (!response?.redirectTo) {
          return;
        }

        setLeavingAfter('deny');
        clearConsentRequest();
        window.location.assign(response.redirectTo);
      },
    },
    tenantAccess ?? keyAccess,
  );
  const { token: keyAccessToken } = useRealmAPIContext(keyAccess);
  const requestError =
    inspectError ?? [approveError, denyError].find((mutationError) => mutationError instanceof APIError && mutationError.status === 400);

  // Inspect as soon as the realm token for keyAccess is available.
  useEffect(() => {
    if (consentRequest && keyAccess && keyAccessToken) {
      inspect({ request: consentRequest.request });
    }
  }, [consentRequest, inspect, keyAccess, keyAccessToken]);

  // A 400 means the request is bad, expired or already used. Drop it so a refresh does not resend it.
  useEffect(() => {
    if (requestError instanceof APIError && requestError.status === 400) {
      clearConsentRequest();
    }
  }, [requestError]);

  useEffect(() => {
    if (approveError && !(approveError instanceof APIError && approveError.status === 400)) {
      addToast({
        id: 'oauthConsentGrantFailed',
        content: approveError.message,
        variant: 'error',
      });
    }
  }, [addToast, approveError]);

  useEffect(() => {
    if (denyError && !(denyError instanceof APIError && denyError.status === 400)) {
      addToast({
        id: 'oauthConsentDenyFailed',
        content: denyError.message,
        variant: 'error',
      });
    }
  }, [addToast, denyError]);

  const handleGrant = useCallback(
    (agentId: string) => {
      if (consentRequest) {
        approve({ request: consentRequest.request, agentId });
      }
    },
    [approve, consentRequest],
  );

  const handleDeny = useCallback(() => {
    if (consentRequest) {
      deny({ request: consentRequest.request });
    }
  }, [deny, consentRequest]);

  if (!consentRequest) {
    return (
      <AuthLayout>
        <ConsentErrorCard align="center" description="Start the connection again from your application." title="Nothing to Authorize" />
      </AuthLayout>
    );
  }

  if (!keyAccess) {
    return (
      <AuthLayout>
        <ConsentErrorCard
          description={`Your account can't grant access on ${getHostname(consentRequest.iss)}.`}
          onReturnToPortal={clearConsentRequest}
        />
      </AuthLayout>
    );
  }

  if (requestError) {
    return (
      <AuthLayout>
        <ConsentErrorCard description="This request is no longer valid. Start the connection again from your application." />
      </AuthLayout>
    );
  }

  if (!inspection) {
    return (
      <AuthLayout>
        <Typography as="h1" lineHeight="1.5rem" size="1.5rem" variant="heading" weight="bold">
          <Skeleton />
        </Typography>

        <VerticalStack gap={4}>
          <Skeleton />
          <Skeleton />
        </VerticalStack>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <ConsentForm
        clientName={inspection.clientName}
        error={approveError}
        isDenying={isDenying || leavingAfter === 'deny'}
        isPending={isApproving || leavingAfter === 'approve'}
        onDeny={handleDeny}
        onGrant={handleGrant}
        onTenantAccessChange={setTenantAccess}
        redirectUri={inspection.redirectUri}
        tenantAccess={tenantAccess}
        tenantAccesses={tenantAccesses}
      />
    </AuthLayout>
  );
}
